import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { API, IQuizSessionsResponse, IStringDynamicPrototype, SESSION_STATE } from 'src/types'
import { getHttp, postHttp } from 'src/api'
import { init } from '../facebookPixelService'
import { ACTION_APP, ga4EventApp, gaEventApp } from '../googleAnalyticsService'
import { SegmentAction, segmentTracking } from '../segment'
import { isDateFirstEntry, QUIZ_ENTRY_DATE_FIRST } from '../quizEntry'
import { PATH_API, PATH_URL } from '../path'
import { ENV } from 'src/config/env'

import { useAppContext } from './App.context'
import { generateSessionID } from './helper'

export const SESSION_NAME_LOCAL = 'sessionID'

export const useAppBase = () => {
  const {
    appData,
    setSessionID,
    setStoreURLParameters,
    handleChangeAppData,
    setQuizSession,
    setQuizExperience,
    setExperiment,
  } = useAppContext()
  const { isLoading, countLoading, countLoadingTestimonial, hasSessionError, proposalLoadingStage } = appData

  const location = useLocation()

  const currentParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    if (location.pathname.includes(PATH_URL.MICRO_WEDDING)) {
      handleChangeAppData({ isMicroWedding: true })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    const mergeTempSessionData = (values: Record<string, unknown>) => {
      localStorage.setItem(
        'tempPoppySessionData',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}'),
          ...values,
        })
      )
    }

    const checkSessionId = async (sessionID: string): Promise<string> => {
      let newSessionID = sessionID

      const serviceabilityData = currentParams.get('serviceabilityData')
      // Use the full window.location.search if serviceabilityData exists, so that URL params are included when we create a session for serviceability
      const queryString = serviceabilityData ? window.location.search : ''
      // Check if this is a Pinterest OAuth callback - if so, prioritize session ID from localStorage
      const isRedirectFromPinterest = currentParams.get('code') && window.location.pathname.includes('pinterest')

      const fetchAndApplySession = async (idToCheck: string): Promise<string> => {
        let appliedSessionID = idToCheck
        const sessionResult: API<IQuizSessionsResponse> = await getHttp(
          `${PATH_API.getSessionId(idToCheck)}${queryString}`
        )
        mergeTempSessionData({ sessionId: idToCheck, skipServiceability: sessionResult.data.skipServiceability })
        switch (sessionResult.data.sessionState) {
          case SESSION_STATE.SUBMITTED:
            console.log('[Session ID] Session already submitted, generating new session ID')
            appliedSessionID = generateSessionID()
            if (sessionResult.data?.proposal?.hadProposal) {
              sessionStorage.setItem('proposalUrl', sessionResult.data.proposal.proposalUrl)
              window.location.href = PATH_URL.EXISTING_PROPOSAL_AFTER_SUBMITTED
            }
            // Without registering it server-side a returning visitor carries a never-created id and
            // the next bootstrap GET fails with a 400, leaving a session-less app.
            await postHttp(PATH_API.createSessionId(), { sessionId: appliedSessionID })
            mergeTempSessionData({ sessionId: appliedSessionID, skipServiceability: false })
            break
          default:
            break
        }
        setQuizExperience(sessionResult.data.quizExperience)
        setExperiment(sessionResult.data.experiment)
        setQuizSession(sessionResult.data)
        return appliedSessionID
      }

      try {
        if (serviceabilityData) handleChangeAppData({ isLoading: true })

        if (isRedirectFromPinterest) {
          console.log('[Session ID] Detected Pinterest OAuth callback, prioritizing localStorage session ID')
          const storedSessionId = localStorage.getItem(SESSION_NAME_LOCAL)
          if (storedSessionId) {
            console.log('[Session ID] Using stored session ID from localStorage:', storedSessionId)
            newSessionID = storedSessionId
          }
        }

        newSessionID = await fetchAndApplySession(newSessionID)
      } catch (error) {
        console.log('[checkSessionId]', error)

        // If we're in Pinterest flow, try harder to recover the session ID
        if (isRedirectFromPinterest) {
          console.log('[Session ID] Pinterest OAuth error - trying to recover session ID from localStorage')
          const storedSessionId = localStorage.getItem(SESSION_NAME_LOCAL)
          if (storedSessionId) {
            return storedSessionId
          }
        }

        // Clear the stale id FIRST so a refresh always starts clean, then create a fresh session and
        // retry the GET once. A throw here propagates to initSessionID, which surfaces the error
        // state rather than leaving an empty sessionID and silently dead submit handlers.
        localStorage.removeItem(SESSION_NAME_LOCAL)
        newSessionID = generateSessionID()
        await postHttp(PATH_API.createSessionId(), { sessionId: newSessionID })
        setExperiment([])

        mergeTempSessionData({ skipServiceability: false })

        newSessionID = await fetchAndApplySession(newSessionID)
      } finally {
        handleChangeAppData({ isLoading: false })
      }

      return newSessionID
    }

    const initSessionID = async (searchParamsObject: IStringDynamicPrototype) => {
      // First check localStorage, then URL params
      let sessionID = localStorage.getItem(SESSION_NAME_LOCAL) || searchParamsObject[SESSION_NAME_LOCAL]

      if (!sessionID || searchParamsObject.quizData) {
        console.log('[Session ID] Generating new session ID', {
          hadExistingSession: !!sessionID,
          hasQuizData: !!searchParamsObject.quizData,
        })
        sessionID = generateSessionID()
        setExperiment([])
        // history.push(PATH_URL.SWIPING)
      } else {
        console.log('[Session ID] Using existing session ID', sessionID)
        try {
          sessionID = await checkSessionId(sessionID)
        } catch (error) {
          // Bootstrap failed even after creating a fresh session and retrying once. Don't store a
          // dead id (checkSessionId already cleared the stale one) and don't leave an app that looks
          // alive with dead submit handlers — surface the error state instead.
          console.log('[Session ID] Session bootstrap failed after create + retry', error)
          handleChangeAppData({ hasSessionError: true })
          return
        }
      }

      localStorage.setItem(SESSION_NAME_LOCAL, sessionID)
      setSessionID(sessionID)
    }
    const handleStoreURL = () => {
      let searchParamsObject: IStringDynamicPrototype = {}
      if (location.search) {
        searchParamsObject = Object.fromEntries(new URLSearchParams(location.search))
        setStoreURLParameters(searchParamsObject)
      }
      return searchParamsObject
    }

    const checkRunInitApp = () => {
      const urlDontCheck = [
        PATH_URL.YOUR_POPPY_PROPOSAL,
        PATH_URL.SCHEDULE_YOUR_CONSULTATION,
        PATH_URL.CALL_SCHEDULED,
        PATH_URL.$500_CUSTOMER,
        '/moodboard/',
        '/schedule-a-call/',
        '/post-moodboard/',
      ]
      const pathCheck = window.location.pathname
      return !urlDontCheck.some((e) => pathCheck.includes(e))
    }

    const initApp = async () => {
      handleChangeAppData({ isLoading: true })
      const searchParamsObject = handleStoreURL()
      await initSessionID(searchParamsObject)
      handleChangeAppData({ isLoading: false })
    }

    if (checkRunInitApp()) {
      initApp()
    } else {
      handleChangeAppData({ isLoading: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    init()
    const params = new URLSearchParams(window.location.search)
    // don't fire analytics on quiz load when returning from pinterest oauth or when importing pins
    //    this is kind of ugly on the import pins part, but for whatever reason we do a full page load there...
    if (!params.has('code') && !window.location.href.includes('import-pins')) {
      gaEventApp(ACTION_APP.QUIZ_LOADED)
      ga4EventApp(ACTION_APP.QUIZ_LOADED)

      // Track Help Center → Quiz attribution
      const referrer = document.referrer
      if (referrer && referrer.includes('/help/')) {
        try {
          const referrerPath = new URL(referrer).pathname
          ga4EventApp(ACTION_APP.QUIZ_FROM_HELP_CENTER, {
            referring_article: referrerPath,
            referrer_url: referrer,
          })
        } catch (e) {
          // Ignore malformed referrer URLs
        }
      }

      segmentTracking(
        SegmentAction.SQ_QUIZ_LOADED,
        currentParams.has('quizData') ? { x4: 'skipped_details' } : { x4: 'disabled' }
      )
      // Variant-only. The default quiz does not push quiz_entry, so existing
      // dataLayer events stay as they are. Control reads as palette_first by
      // the property being absent.
      if (isDateFirstEntry(window.location.search)) {
        const browserWindow = window as Window & { dataLayer?: Record<string, string>[] }
        const dataLayer = (browserWindow.dataLayer = browserWindow.dataLayer || [])
        dataLayer.push({ quiz_entry: QUIZ_ENTRY_DATE_FIRST })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isLoading: isLoading || (countLoading || 0) > 0,
    isLoadingTestimonial: (countLoadingTestimonial || 0) > 0,
    hasSessionError: !!hasSessionError,
    proposalLoadingStage,
  }
}
